/* pp-theme.js — All vanilla JS for Penguin Productions theme.
   No jQuery, no libraries. Loaded with defer in theme.liquid. */

'use strict';

document.addEventListener('DOMContentLoaded', function () {

  // ═══════════════════════════════════════════════════════════
  // 0. ANNOUNCEMENT BAR DISMISS
  // ✕ button hides the bar with a transition and saves the
  // preference to localStorage so it stays hidden on return visits.
  // The initial html.pp-has-bar class is set by an inline script in
  // the section itself (runs before first paint, no layout flash).
  // ═══════════════════════════════════════════════════════════

  (function announcementBar() {
    const bar = document.getElementById('pp-announcement-bar');
    const btn = document.getElementById('pp-announcement-dismiss');
    if (!bar || !btn) return;

    const KEY = 'pp_ann_dismissed';

    // If already dismissed (but Liquid still rendered the bar),
    // hide it immediately without animation.
    let wasDismissed = false;
    try { wasDismissed = !!localStorage.getItem(KEY); } catch(e) {}
    if (wasDismissed) {
      bar.style.display = 'none';
      document.documentElement.classList.remove('pp-has-bar');
      return;
    }

    btn.addEventListener('click', function () {
      try { localStorage.setItem(KEY, '1'); } catch(e) {}
      bar.classList.add('is-dismissed');
      document.documentElement.classList.remove('pp-has-bar');
      bar.addEventListener('transitionend', function () {
        bar.style.display = 'none';
      }, { once: true });
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 1. MOBILE NAV TOGGLE
  // Opens/closes hamburger menu, traps focus, closes on Escape.
  // ═══════════════════════════════════════════════════════════

  (function mobileNav() {
    const hamburger  = document.querySelector('.pp-header__hamburger');
    const mobileNav  = document.querySelector('.pp-mobile-nav');
    const closeBtn   = document.querySelector('.pp-mobile-nav__close');
    const navLinks   = document.querySelectorAll('.pp-mobile-nav__link, .pp-mobile-nav__cta a');

    if (!hamburger || !mobileNav) return;

    function openNav() {
      mobileNav.classList.add('is-open');
      mobileNav.setAttribute('aria-hidden', 'false');
      mobileNav.removeAttribute('inert');
      hamburger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('nav-open');
      closeBtn && closeBtn.focus();
    }

    function closeNav() {
      mobileNav.classList.remove('is-open');
      mobileNav.setAttribute('aria-hidden', 'true');
      mobileNav.setAttribute('inert', '');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('nav-open');
      hamburger.focus();
    }

    hamburger.addEventListener('click', function () {
      const isOpen = this.getAttribute('aria-expanded') === 'true';
      isOpen ? closeNav() : openNav();
    });

    closeBtn && closeBtn.addEventListener('click', closeNav);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) {
        closeNav();
      }
    });

    // Trap focus within the open mobile nav
    mobileNav.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !mobileNav.classList.contains('is-open')) return;

      const focusable = Array.from(
        mobileNav.querySelectorAll('a, button, [tabindex="0"]')
      ).filter(el => !el.disabled);

      if (!focusable.length) return;

      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 2. STICKY HEADER
  // Adds .is-sticky after 80px scroll. Uses rAF for performance.
  // ═══════════════════════════════════════════════════════════

  (function stickyHeader() {
    const header = document.querySelector('.pp-header');
    if (!header) return;

    let ticking = false;

    function updateSticky() {
      if (window.scrollY > 80) {
        header.classList.add('is-sticky');
      } else {
        header.classList.remove('is-sticky');
      }
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(updateSticky);
        ticking = true;
      }
    }, { passive: true });
  })();

  // ═══════════════════════════════════════════════════════════
  // 3. HERO SVG UNDERLINE ANIMATION
  // Draws the wobbly lemon underline on page load via
  // stroke-dashoffset. Skips if prefers-reduced-motion.
  // ═══════════════════════════════════════════════════════════

  (function heroUnderline() {
    const svgs = document.querySelectorAll('.pp-highlight__svg');
    if (!svgs.length) return;

    // Skip draw animation if reduced motion preferred — SVGs are visible by default via CSS
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    svgs.forEach(function (svg, i) {
      const path = svg.querySelector('path');
      if (!path) return;
      const length = path.getTotalLength();
      // Guard: if length is 0 the SVG isn't painted yet — leave it visible, skip animation
      if (!length) return;
      path.style.strokeDasharray  = length;
      path.style.strokeDashoffset = length;
      const delay = 0.3 + i * 0.15;
      path.style.transition = 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1) ' + delay + 's';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          path.style.strokeDashoffset = '0';
        });
      });
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 4. TESTIMONIAL CAROUSEL
  // CSS scroll-snap carousel — JS handles prev/next buttons,
  // dot indicators, and aria-live announcements.
  // ═══════════════════════════════════════════════════════════

  (function testimonialCarousel() {
    const carousel = document.querySelector('.pp-testimonials__track');
    if (!carousel) return;

    const slides   = Array.from(carousel.querySelectorAll('.pp-testimonials__slide'));
    const prevBtn  = document.querySelector('.pp-testimonials__prev');
    const nextBtn  = document.querySelector('.pp-testimonials__next');
    const dots     = document.querySelectorAll('.pp-testimonials__dot');
    const liveReg  = document.querySelector('.pp-testimonials__live');

    if (!slides.length) return;

    let currentIndex = 0;

    function getVisibleCount() {
      const w = window.innerWidth;
      if (w >= 1024) return 3;
      if (w >= 640)  return 2;
      return 1;
    }

    function goTo(index) {
      const count   = slides.length;
      const visible = getVisibleCount();
      const maxIndex = Math.max(0, count - visible);

      currentIndex = Math.max(0, Math.min(index, maxIndex));

      const slideWidth = slides[0].offsetWidth;
      carousel.scrollTo({ left: slideWidth * currentIndex, behavior: 'smooth' });

      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === currentIndex);
        dot.setAttribute('aria-current', i === currentIndex ? 'true' : 'false');
      });

      if (liveReg) {
        liveReg.textContent = `Showing testimonial ${currentIndex + 1} of ${count}`;
      }

      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex >= maxIndex;
    }

    prevBtn && prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
    nextBtn && nextBtn.addEventListener('click', () => goTo(currentIndex + 1));

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => goTo(i));
    });

    goTo(0);
  })();

  // ═══════════════════════════════════════════════════════════
  // 4b. PORTFOLIO PREVIEW MOBILE CAROUSEL
  // Activates only below 640px. Prev/next buttons scroll the
  // snap container; resets on resize to desktop.
  // ═══════════════════════════════════════════════════════════

  (function portfolioPreviewCarousel() {
    const track   = document.querySelector('.pp-portfolio-preview__grid');
    const prevBtn = document.querySelector('.pp-portfolio-preview__prev');
    const nextBtn = document.querySelector('.pp-portfolio-preview__next');
    if (!track || !prevBtn || !nextBtn) return;

    const slides = Array.from(track.querySelectorAll('.pp-portfolio-preview__slide'));
    if (!slides.length) return;

    let currentIndex = 0;

    function isMobile() { return window.innerWidth < 640; }

    function goTo(index) {
      if (!isMobile()) return;
      currentIndex = Math.max(0, Math.min(index, slides.length - 1));
      track.scrollTo({ left: slides[currentIndex].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      prevBtn.disabled = currentIndex === 0;
      nextBtn.disabled = currentIndex >= slides.length - 1;
    }

    prevBtn.addEventListener('click', function () { goTo(currentIndex - 1); });
    nextBtn.addEventListener('click', function () { goTo(currentIndex + 1); });

    window.addEventListener('resize', function () {
      if (!isMobile()) { track.scrollLeft = 0; currentIndex = 0; }
    });

    if (isMobile()) goTo(0);
  })();

  // ═══════════════════════════════════════════════════════════
  // 5. PORTFOLIO FILTER TABS
  // Filters .pp-portfolio-card elements by data-type attribute.
  // Announces filter change to screen readers via aria-live.
  // ═══════════════════════════════════════════════════════════

  (function portfolioFilter() {
    const filterBar = document.querySelector('.pp-portfolio-filter');
    if (!filterBar) return;

    const tabs      = filterBar.querySelectorAll('.pp-portfolio-filter__tab');
    const cards     = document.querySelectorAll('.pp-portfolio-card[data-type]');
    const liveReg   = document.querySelector('.pp-portfolio-filter__live');

    tabs.forEach(tab => {
      tab.addEventListener('click', function () {
        const filter = this.dataset.filter;

        tabs.forEach(t => {
          t.setAttribute('aria-selected', 'false');
          t.classList.remove('is-active');
        });
        this.setAttribute('aria-selected', 'true');
        this.classList.add('is-active');

        let visible = 0;

        cards.forEach(card => {
          const match = filter === 'all' || card.dataset.type === filter;
          card.style.display = match ? '' : 'none';
          if (match) visible++;
        });

        if (liveReg) {
          liveReg.textContent = `Showing ${visible} project${visible !== 1 ? 's' : ''}`;
        }
      });
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 5b. VARIANT SELECTION → live price, sale badge, image, and ATC state
  // No live network request — the full variant list is embedded in the
  // page as JSON so this just looks up the matching variant locally.
  // ═══════════════════════════════════════════════════════════

  (function variantSelection() {
    const groups = document.querySelectorAll('[data-option-selector]');
    if (!groups.length) return;

    const variantsScript = document.querySelector('[data-product-variants]');
    if (!variantsScript) return;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      return;
    }

    const idInput      = document.getElementById('pp-product-variant-id');
    const priceCurrent = document.querySelector('[data-price-current]');
    const priceCompare = document.querySelector('[data-price-compare]');
    const saleBadge     = document.querySelector('[data-sale-badge]');
    const atcButtons    = document.querySelectorAll('[data-atc-button]');

    function formatMoney(cents) {
      const moneyFormat = (window.ppConfig && window.ppConfig.moneyFormat) || '£{{amount}}';
      const amount = (cents / 100).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
    }

    function groupValue(group) {
      const checked = group.querySelector('input:checked');
      return checked ? checked.value : null;
    }

    function findVariant() {
      const selected = Array.from(groups)
        .sort(function (a, b) { return a.dataset.optionPosition - b.dataset.optionPosition; })
        .map(groupValue);

      return variants.find(function (variant) {
        const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        return selected.every(function (value, i) { return options[i] === value; });
      });
    }


    function updateImage(variant) {
      if (!variant || !variant.featured_image) return;
      const thumb = document.querySelector('[data-image-id="' + variant.featured_image.id + '"]');
      if (thumb && !thumb.classList.contains('is-active')) thumb.click();
    }

    function updateUI() {
      const variant = findVariant();

      if (!variant) {
        atcButtons.forEach(function (btn) {
          btn.disabled = true;
          btn.textContent = btn.dataset.labelUnavailable || 'Unavailable';
        });
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price);

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (priceCompare) {
        priceCompare.hidden = !onSale;
        if (onSale) priceCompare.textContent = formatMoney(variant.compare_at_price);
      }
      if (saleBadge) saleBadge.hidden = !onSale;

      atcButtons.forEach(function (btn) {
        btn.disabled = !variant.available;
        btn.textContent = variant.available
          ? (btn.dataset.labelAvailable || 'Add to bag')
          : (btn.dataset.labelSoldOut || 'Sold out');
      });

      updateImage(variant);
    }

    groups.forEach(function (group) {
      group.addEventListener('change', updateUI);
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 5c. QUANTITY STEPPER
  // ═══════════════════════════════════════════════════════════

  (function quantityStepper() {
    document.querySelectorAll('.pp-qty-stepper').forEach(function (stepper) {
      const input = stepper.querySelector('.pp-qty-stepper__input');
      const decrease = stepper.querySelector('[data-qty-decrease]');
      const increase = stepper.querySelector('[data-qty-increase]');
      if (!input) return;

      function setValue(value) {
        const min = parseInt(input.min, 10) || 1;
        input.value = Math.max(min, value);
      }

      decrease && decrease.addEventListener('click', function () {
        setValue((parseInt(input.value, 10) || 1) - 1);
      });

      increase && increase.addEventListener('click', function () {
        setValue((parseInt(input.value, 10) || 1) + 1);
      });

      input.addEventListener('change', function () {
        setValue(parseInt(input.value, 10) || 1);
      });
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 6. PRODUCT IMAGE GALLERY
  // Thumbnail click updates main image with a cross-fade.
  // ═══════════════════════════════════════════════════════════

  (function productGallery() {
    const mainImg = document.querySelector('.pp-product-media__main img');
    if (!mainImg) return;

    const thumbs = document.querySelectorAll('.pp-product-media__thumb');

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', function () {
        const src    = this.dataset.src;
        const srcset = this.dataset.srcset;
        const alt    = this.dataset.alt;

        mainImg.style.opacity = '0';
        mainImg.style.transition = 'opacity 0.2s ease';

        setTimeout(function () {
          mainImg.src = src;
          if (srcset) mainImg.srcset = srcset;
          if (alt) mainImg.alt = alt;
          mainImg.style.opacity = '1';
        }, 200);

        thumbs.forEach(t => t.classList.remove('is-active'));
        this.classList.add('is-active');
      });
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 7. PRODUCT STICKY ATC BAR
  // Shows fixed bottom bar when user scrolls past main ATC.
  // Hides when footer is in view.
  // ═══════════════════════════════════════════════════════════

  (function stickyAtc() {
    const mainAtc   = document.querySelector('.pp-product-info__atc');
    const stickyBar = document.querySelector('.pp-sticky-atc');
    const footer    = document.querySelector('.pp-footer');

    if (!mainAtc || !stickyBar) return;

    let ticking = false;

    function update() {
      const atcRect    = mainAtc.getBoundingClientRect();
      const footerTop  = footer ? footer.getBoundingClientRect().top : Infinity;
      const pastAtc    = atcRect.bottom < 0;
      const nearFooter = footerTop < window.innerHeight;

      stickyBar.classList.toggle('is-visible', pastAtc && !nearFooter);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  })();

  // ═══════════════════════════════════════════════════════════
  // 7b. B2B FORM INLINE VALIDATION
  // Runs before aggregation (capture phase) so invalid submissions
  // never reach the aggregation handler or Shopify's server.
  // ═══════════════════════════════════════════════════════════

  (function b2bFormValidation() {
    const form = document.getElementById('b2b-enquiry-form');
    if (!form) return;

    function fieldValid(input) {
      if (input.type === 'email') {
        return input.value.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value);
      }
      return input.value.trim().length > 0;
    }

    function setFieldState(input, valid) {
      const field = input.closest('.pp-form__field');
      if (field) field.classList.toggle('pp-form__field--error', !valid);
      const errorEl = document.getElementById(input.getAttribute('aria-describedby'));
      if (errorEl) errorEl.hidden = valid;
    }

    const requiredInputs = Array.from(form.querySelectorAll('input[required], textarea[required]'));

    requiredInputs.forEach(function (input) {
      input.addEventListener('blur', function () {
        setFieldState(this, fieldValid(this));
      });
      input.addEventListener('input', function () {
        if (fieldValid(this)) setFieldState(this, true);
      });
    });

    form.addEventListener('submit', function (e) {
      let allValid = true;
      let firstInvalid = null;

      requiredInputs.forEach(function (input) {
        const valid = fieldValid(input);
        setFieldState(input, valid);
        if (!valid) {
          allValid = false;
          if (!firstInvalid) firstInvalid = input;
        }
      });

      if (!allValid) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (firstInvalid) firstInvalid.focus();
      }
    }, true);
  })();

  // ═══════════════════════════════════════════════════════════
  // 8. B2B FORM FIELD AGGREGATION
  // Collects all custom fields into contact[body] before submit
  // so the full enquiry lands in Shopify contact email.
  // ═══════════════════════════════════════════════════════════

  (function b2bFormAggregation() {
    const form = document.getElementById('b2b-enquiry-form');
    if (!form) return;

    form.addEventListener('submit', function () {
      const desc = document.getElementById('contact-body');
      const fields = [
        ['Company',          document.getElementById('pp-field-company')],
        ['Phone',            document.getElementById('pp-field-phone')],
        ['Project Type',     document.getElementById('pp-field-project-type')],
        ['Quantity',         document.getElementById('pp-field-quantity')],
        ['Timeline',         document.getElementById('pp-field-timeline')],
        ['How They Heard',   document.getElementById('pp-field-referral')],
      ];

      const sampleEl    = form.querySelector('[name="pp-field-sample"]:checked');
      const sample      = sampleEl ? sampleEl.value : 'Not answered';
      const marketingEl = document.getElementById('pp-marketing-consent-b2b');

      const bar  = '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━';
      const line = '────────────────────────────────────────';
      const pad  = function (label) { return (label + ':').padEnd(18); };

      const rows = fields
        .filter(function (pair) { return pair[1]; })
        .map(function (pair) { return pad(pair[0]) + (pair[1].value || 'Not provided'); });

      rows.push(pad('Sample Required') + sample);
      if (marketingEl) {
        rows.push(pad('Marketing consent') + (marketingEl.checked ? 'Yes — opted in' : 'No'));
      }

      const artworkUrl = document.getElementById('pp-b2b-artwork-url');
      const parts = [
        bar,
        'NEW B2B ENQUIRY — PENGUIN PRODUCTIONS',
        bar,
        '',
        rows.join('\n'),
        '',
      ];

      if (artworkUrl && artworkUrl.value) {
        parts.push(line, 'ARTWORK', line, artworkUrl.value, '');
      }

      const descValue = desc ? desc.value.trim() : '';
      if (descValue) {
        parts.push(line, 'PROJECT DESCRIPTION', line, descValue, '');
      }

      parts.push(bar);

      const hiddenBody = document.getElementById('pp-aggregated-body');
      if (hiddenBody) hiddenBody.value = parts.join('\n');

      // Prevent the textarea's own name from sending a duplicate body field
      if (desc) desc.removeAttribute('name');
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 8aa. CONTACT FORM SUBMIT HANDLER
  // Appends uploaded file URL and marketing consent preference
  // to contact[body] so both arrive in the notification email.
  // ═══════════════════════════════════════════════════════════

  (function contactFormSubmit() {
    const form = document.getElementById('contact-form');
    if (!form) return;

    form.addEventListener('submit', function () {
      const body         = form.querySelector('[name="contact[body]"]');
      const fileUrlInput = document.getElementById('pp-contact-file-url');
      const marketingEl  = document.getElementById('pp-marketing-consent-contact');

      if (!body) return;

      const additions = [];
      if (fileUrlInput && fileUrlInput.value) {
        additions.push('Attached file: ' + fileUrlInput.value);
      }
      if (marketingEl) {
        additions.push('Marketing consent: ' + (marketingEl.checked ? 'Yes — opted in' : 'No'));
      }
      if (additions.length) {
        const separator = body.value.trim() ? '\n\n' : '';
        body.value += separator + additions.join('\n');
      }
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 8ab. ARTWORK UPLOAD FORM AGGREGATION
  // Folds order number, notes, and file URL into contact[body].
  // ═══════════════════════════════════════════════════════════

  (function artworkFormAggregation() {
    const form = document.getElementById('pp-artwork-form');
    if (!form) return;

    form.addEventListener('submit', function () {
      const orderField  = document.getElementById('artwork-order-number');
      const notesField  = document.getElementById('artwork-notes');
      const fileUrlEl   = document.getElementById('pp-artwork-submission-url');
      const marketingEl = document.getElementById('pp-marketing-consent-artwork');
      const hiddenBody  = document.getElementById('pp-artwork-aggregated-body');
      if (!hiddenBody) return;

      const lines = [];
      if (orderField && orderField.value.trim()) lines.push('Order number: ' + orderField.value.trim());
      if (notesField && notesField.value.trim())  lines.push('Notes: ' + notesField.value.trim());
      if (fileUrlEl  && fileUrlEl.value)          lines.push('Artwork file: ' + fileUrlEl.value);
      if (marketingEl) {
        lines.push('Marketing consent: ' + (marketingEl.checked ? 'Yes — opted in' : 'No'));
      }

      hiddenBody.value = lines.join('\n');
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 8a. REVIEW FORM FIELD AGGREGATION
  // Same reasoning as the B2B form above — folds the star rating into
  // contact[body] since Shopify's contact form doesn't surface custom
  // field names in the notification email.
  // ═══════════════════════════════════════════════════════════

  (function reviewFormAggregation() {
    var form = document.getElementById('pp-review-form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      var titleEl     = document.getElementById('pp-review-title');
      var reviewEl    = document.getElementById('pp-review-text');
      var ratingEl    = form.querySelector('input[name="rating"]:checked');
      var marketingEl = document.getElementById('pp-marketing-consent-review');
      var errorEl     = document.getElementById('pp-review-js-error');

      var reviewVal = reviewEl ? reviewEl.value.trim() : '';

      if (!reviewVal) {
        e.preventDefault();
        if (errorEl) { errorEl.textContent = 'Please write your review before submitting.'; errorEl.style.display = ''; }
        if (reviewEl) reviewEl.focus();
        return;
      }
      if (errorEl) errorEl.style.display = 'none';

      var ratingInt = ratingEl ? parseInt(ratingEl.value, 10) : 5;
      var stars     = '★'.repeat(ratingInt) + '☆'.repeat(5 - ratingInt);
      var date      = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      var pad       = function (label) { return (label + ':').padEnd(22); };

      var bar  = '━'.repeat(44);
      var line = '─'.repeat(44);

      var parts = [
        bar,
        '  NEW REVIEW — PENGUIN PRODUCTIONS',
        bar,
        '',
        pad('Rating') + ratingInt + ' / 5  ' + stars,
        pad('Date')   + date,
      ];

      if (titleEl && titleEl.value.trim()) {
        parts.push(pad('Title') + titleEl.value.trim());
      }

      parts.push('', line, '  REVIEW', line, '');
      parts.push(reviewVal);

      parts.push('', line);
      if (marketingEl) {
        parts.push(pad('Marketing') + (marketingEl.checked ? 'Yes — opted in' : 'No'));
      }
      parts.push('', bar);
      parts.push('');
      parts.push('TO APPROVE: Shopify Admin → Content → Metaobjects → Review → Add entry');
      parts.push('Then set status to Published to display on the site.');
      parts.push('');
      parts.push(bar);

      var hiddenBody = document.getElementById('pp-review-aggregated-body');
      if (hiddenBody) hiddenBody.value = parts.join('\n');
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 8b. IMAGE UPLOAD (imgBB)
  // Handles file uploads on product pages and B2B form.
  // Uploads to imgBB, stores URL in hidden input so it travels
  // with the order / contact submission.
  // Falls back gracefully if no API key is configured.
  // ═══════════════════════════════════════════════════════════

  (function imageUpload() {
    const inputs = document.querySelectorAll('.pp-upload-field__input');
    if (!inputs.length) return;

    const apiKey = window.ppConfig && window.ppConfig.imgbbKey;

    function handleFile(input, zoneEl, statusEl, urlInput) {
      const file = input.files[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        statusEl.textContent = 'File too large — maximum size is 10MB.';
        statusEl.dataset.state = 'error';
        input.value = '';
        return;
      }

      statusEl.dataset.state = 'uploading';
      statusEl.textContent = 'Uploading ' + file.name + '…';

      if (!apiKey) {
        statusEl.textContent = file.name + ' selected. We may contact you to request the file.';
        statusEl.dataset.state = 'success';
        if (urlInput) urlInput.value = '[File: ' + file.name + ']';
        if (zoneEl) zoneEl.classList.add('has-file');
        return;
      }

      const fd = new FormData();
      fd.append('image', file);
      fd.append('key', apiKey);

      fetch('https://api.imgbb.com/1/upload', { method: 'POST', body: fd })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.success) {
            if (urlInput) urlInput.value = data.data.url;
            statusEl.textContent = '✓ ' + file.name + ' uploaded.';
            statusEl.dataset.state = 'success';
            if (zoneEl) zoneEl.classList.add('has-file');
          } else {
            throw new Error('Upload rejected');
          }
        })
        .catch(function () {
          statusEl.textContent = 'Upload failed. Please try again or email your file directly.';
          statusEl.dataset.state = 'error';
        });
    }

    inputs.forEach(function (input) {
      const uploadField = input.closest('.pp-upload-field');
      const zoneEl      = input.closest('.pp-upload-field__zone');
      const statusEl    = uploadField ? uploadField.querySelector('.pp-upload-field__status') : null;
      const urlInput    = uploadField ? uploadField.querySelector('input[type="hidden"]') : null;

      if (!statusEl) return;

      input.addEventListener('change', function () {
        handleFile(this, zoneEl, statusEl, urlInput);
      });

      if (zoneEl) {
        zoneEl.addEventListener('dragover', function (e) {
          e.preventDefault();
          this.classList.add('is-dragover');
        });
        zoneEl.addEventListener('dragleave', function () {
          this.classList.remove('is-dragover');
        });
        zoneEl.addEventListener('drop', function (e) {
          e.preventDefault();
          this.classList.remove('is-dragover');
          if (e.dataTransfer.files.length) {
            const dt = new DataTransfer();
            dt.items.add(e.dataTransfer.files[0]);
            input.files = dt.files;
            handleFile(input, zoneEl, statusEl, urlInput);
          }
        });
      }
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 9. ACCORDION
  // Generic accordion using aria-expanded / aria-controls.
  // Animated with CSS grid-template-rows transition.
  // ═══════════════════════════════════════════════════════════

  (function accordion() {
    document.querySelectorAll('.pp-accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', function () {
        const expanded = this.getAttribute('aria-expanded') === 'true';
        const panelId  = this.getAttribute('aria-controls');
        const panel    = panelId ? document.getElementById(panelId) : null;

        // Allow multiple open at once — each trigger is independent
        this.setAttribute('aria-expanded', String(!expanded));

        if (panel) {
          panel.setAttribute('aria-hidden', String(expanded));
        }
      });
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 8c. CART (AJAX drawer)
  // Add-to-cart, quantity change, and remove all go through Shopify's
  // Cart AJAX API (/cart/add.js, /cart/change.js, /cart.js) so the page
  // never reloads. Checkout itself is a plain link to /checkout —
  // no JS in that path so it can't break the handoff to Shopify.
  // ═══════════════════════════════════════════════════════════

  (function cart() {
    const icon      = document.getElementById('pp-cart-icon');
    const drawer    = document.getElementById('pp-cart-drawer');
    if (!icon || !drawer) return;

    const countEls   = document.querySelectorAll('[data-cart-count]');
    const emptyEl    = drawer.querySelector('[data-cart-empty]');
    const listEl     = drawer.querySelector('[data-cart-list]');
    const footerEl   = drawer.querySelector('[data-cart-footer]');
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    const cartType   = (window.ppConfig && window.ppConfig.cartType) || 'drawer';
    const moneyFormat = (window.ppConfig && window.ppConfig.moneyFormat) || '£{{amount}}';

    function formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
    }

    function openDrawer() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('nav-open');
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('nav-open');
    }

    icon.addEventListener('click', function (e) {
      if (cartType !== 'drawer') return;
      e.preventDefault();
      openDrawer();
    });

    drawer.querySelectorAll('[data-cart-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    function renderItem(item) {
      const variantLine = item.variant_title
        ? '<p class="pp-cart-drawer__item-variant">' + item.variant_title + '</p>'
        : '';
      const imageHtml = item.image
        ? '<img src="' + item.image + '&width=160" alt="' + (item.product_title || '') + '" width="80" height="80" loading="lazy">'
        : '<div class="pp-cart-drawer__item-image-placeholder"></div>';

      return (
        '<li class="pp-cart-drawer__item" data-cart-key="' + item.key + '">' +
          '<a href="' + item.url + '" class="pp-cart-drawer__item-image">' + imageHtml + '</a>' +
          '<div class="pp-cart-drawer__item-info">' +
            '<a href="' + item.url + '" class="pp-cart-drawer__item-title">' + item.product_title + '</a>' +
            variantLine +
            '<div class="pp-cart-drawer__item-qty">' +
              '<button type="button" class="pp-cart-drawer__qty-btn" data-cart-qty-decrease aria-label="Decrease quantity">' +
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
              '</button>' +
              '<span class="pp-cart-drawer__qty-value" data-cart-qty-value>' + item.quantity + '</span>' +
              '<button type="button" class="pp-cart-drawer__qty-btn" data-cart-qty-increase aria-label="Increase quantity">' +
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
              '</button>' +
            '</div>' +
            '<button type="button" class="pp-cart-drawer__remove" data-cart-remove>Remove</button>' +
          '</div>' +
          '<div class="pp-cart-drawer__item-price" data-cart-line-price>' + formatMoney(item.final_line_price) + '</div>' +
        '</li>'
      );
    }

    function renderCart(cartData) {
      countEls.forEach(function (el) {
        el.textContent = cartData.item_count;
        el.hidden = cartData.item_count === 0;
      });

      if (cartData.item_count === 0) {
        if (emptyEl) emptyEl.hidden = false;
        if (listEl) { listEl.hidden = true; listEl.innerHTML = ''; }
        if (footerEl) footerEl.hidden = true;
        return;
      }

      if (emptyEl) emptyEl.hidden = true;
      if (listEl) {
        listEl.hidden = false;
        listEl.innerHTML = cartData.items.map(renderItem).join('');
      }
      if (footerEl) footerEl.hidden = false;
      if (subtotalEl) subtotalEl.textContent = formatMoney(cartData.total_price);
    }

    function fetchCart() {
      return fetch('/cart.js').then(function (res) { return res.json(); });
    }

    function changeItem(key, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (res) { return res.json(); })
        .then(renderCart);
    }

    listEl && listEl.addEventListener('click', function (e) {
      const item = e.target.closest('.pp-cart-drawer__item');
      if (!item) return;
      const key = item.dataset.cartKey;
      const qtyValueEl = item.querySelector('[data-cart-qty-value]');
      const currentQty = qtyValueEl ? parseInt(qtyValueEl.textContent, 10) : 0;

      if (e.target.closest('[data-cart-qty-increase]')) {
        changeItem(key, currentQty + 1);
      } else if (e.target.closest('[data-cart-qty-decrease]')) {
        changeItem(key, Math.max(0, currentQty - 1));
      } else if (e.target.closest('[data-cart-remove]')) {
        changeItem(key, 0);
      }
    });

    document.addEventListener('submit', function (e) {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      e.preventDefault();
      const formData = new FormData(form);

      fetch('/cart/add.js', { method: 'POST', body: formData })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return fetchCart();
        })
        .then(function (cartData) {
          renderCart(cartData);
          if (cartType === 'drawer') openDrawer();
        })
        .catch(function (err) {
          console.error('Add to cart failed', err);
          form.submit();
        });
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 9b. NAV DROPDOWNS
  // Desktop: CSS :hover/:focus-within opens dropdowns for mouse/keyboard
  // users; click toggling here covers touch devices with no hover.
  // Mobile: dropdown triggers expand/collapse an inline sub-list.
  // ═══════════════════════════════════════════════════════════

  (function navDropdowns() {
    const desktopItems = document.querySelectorAll('.pp-header__nav-item--has-dropdown');

    function closeAll() {
      desktopItems.forEach(function (other) {
        other.classList.remove('is-open');
        const t = other.querySelector('.pp-header__nav-trigger');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }

    desktopItems.forEach(function (item) {
      const trigger = item.querySelector('.pp-header__nav-trigger');
      if (!trigger) return;

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        const isOpen = item.classList.contains('is-open');
        closeAll();
        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      // Arrow key navigation within open dropdown
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (!item.classList.contains('is-open')) {
            closeAll();
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
          const firstLink = item.querySelector('.pp-header__nav-dropdown-link');
          if (firstLink) firstLink.focus();
        }
      });

      const dropdown = item.querySelector('.pp-header__nav-dropdown');
      if (dropdown) {
        dropdown.addEventListener('keydown', function (e) {
          const links = Array.from(dropdown.querySelectorAll('.pp-header__nav-dropdown-link'));
          const idx   = links.indexOf(document.activeElement);

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = links[idx + 1];
            if (next) next.focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (idx === 0) {
              trigger.focus();
            } else {
              const prev = links[idx - 1];
              if (prev) prev.focus();
            }
          } else if (e.key === 'Escape') {
            closeAll();
            trigger.focus();
          } else if (e.key === 'Tab' && !e.shiftKey && idx === links.length - 1) {
            // Tab out of last dropdown item — close dropdown naturally
            closeAll();
          }
        });
      }
    });

    document.addEventListener('click', function (e) {
      desktopItems.forEach(function (item) {
        if (!item.contains(e.target)) {
          item.classList.remove('is-open');
          const t = item.querySelector('.pp-header__nav-trigger');
          if (t) t.setAttribute('aria-expanded', 'false');
        }
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      closeAll();
    });

    const mobileItems = document.querySelectorAll('.pp-mobile-nav__item--has-dropdown');

    mobileItems.forEach(function (item) {
      const trigger = item.querySelector('.pp-mobile-nav__trigger');
      if (!trigger) return;

      trigger.addEventListener('click', function () {
        const isOpen = item.classList.contains('is-open');
        item.classList.toggle('is-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  })();

  // ═══════════════════════════════════════════════════════════
  // 10. SCROLL REVEAL
  // Fades sections in as they enter the viewport.
  // Skips entirely if prefers-reduced-motion.
  // ═══════════════════════════════════════════════════════════

  (function scrollReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const elements = document.querySelectorAll('.pp-reveal');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('pp-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    elements.forEach(el => observer.observe(el));
  })();

});
